import { ChangeEvent, useMemo } from "react"
import { useTranslation } from "react-i18next"

import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import TextField from "@/components/form/textField/TextField"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { getTemplateSummarySettings } from "@/app/report/common/sections/summary"

import type { ChartOptionsPanelProps } from "./types"
import { DecimalPlacesSelector, MetricSelector } from "./SharedWidgets"

/**
 * Summary 의 값·카드 모양은 리포트 화면과 같은 컴포넌트가 그린다 (일감 #75). 지표는 처음 추가될 때
 * Report Filters 에서 고른 리포트 템플릿의 Summary 섹션 지표를 가져와 선택된 상태로 두고, 여기서 더하거나 뺀다
 * (2026-09-23 결정 — Table·Graph 와 같은 규칙). 지표마다 제목·계산 방식을 리포트 설정과 같은 구성으로 고친다
 * (2026-10-01 요청) — 비우면 템플릿 제목, 템플릿에 없던 지표는 지표 이름.
 */
export default function SummaryOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const { item, onDataSourceChange, onOptionChange, metricOptions, translateKey } = props
  const { reportTemplate } = usePdfDataContext()

  const templateSettings = useMemo(
    () => getTemplateSummarySettings(reportTemplate) ?? [],
    [reportTemplate],
  )
  const templateMetrics = useMemo(
    () => templateSettings.map((setting) => setting.metric),
    [templateSettings],
  )
  const metrics = item.dataSource.metrics ?? templateMetrics
  const titles = item.options.summaryTitles ?? {}
  const calcTypes = item.options.summaryCalcTypes ?? {}

  return (
    <>
      <MetricSelector
        label={t("metrics", "Metrics")}
        selectedMetrics={metrics}
        metricOptions={metricOptions}
        onAdd={(metricKey) => onDataSourceChange("metrics", [...metrics, metricKey])}
        onRemove={(metricKey) =>
          onDataSourceChange(
            "metrics",
            metrics.filter((key) => key !== metricKey),
          )
        }
        translateKey={translateKey}
        maxCount={6}
      />

      <DecimalPlacesSelector
        value={item.options.decimalPlaces ?? 1}
        onChange={(value) => onOptionChange("decimalPlaces", value)}
      />

      {metrics.map((metricKey) => {
        const fromTemplate = templateSettings.find((setting) => setting.metric === metricKey)
        const calcType =
          calcTypes[metricKey] ?? fromTemplate?.calcType ?? item.dataSource.calcType ?? "average"

        return (
          <div key={metricKey} className="PropertyPanel__summary-item">
            <span className="PropertyPanel__summary-item-metric">{translateKey(metricKey)}</span>
            <TextField
              value={titles[metricKey] ?? fromTemplate?.title ?? ""}
              placeholder={fromTemplate?.title ?? translateKey(metricKey)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onOptionChange("summaryTitles", { ...titles, [metricKey]: e.target.value })
              }
            />
            <RadioGroup
              direction="row"
              items={[
                { label: t("average", "Average"), value: "average" },
                { label: t("total", "Total"), value: "total" },
              ]}
              value={calcType}
              onChange={(_: ChangeEvent<HTMLInputElement>, value?: string) => {
                if (value === "average" || value === "total") {
                  onOptionChange("summaryCalcTypes", { ...calcTypes, [metricKey]: value })
                }
              }}
            />
          </div>
        )
      })}
    </>
  )
}
